import { faEye } from "@fortawesome/pro-solid-svg-icons/faEye";
import { faEyeSlash } from "@fortawesome/pro-solid-svg-icons/faEyeSlash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, Button, CardContent, Checkbox, Container, FormControlLabel, Grid, Stack, TextField } from "@mui/material";
import { APP_STORE_GOOGLE_LINK } from "app/appConstants";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { MprcCard, MprcCardActions, MprcLine, MprcLink, MprcMessageBlock, MprcRequestIndicator, MprcSpinnerIndicator, MprcTextMuted } from "components";
import { AccountAccessType, AccountLoginCheckResult, ResourceModel } from "generated/CoreApi_v1";
import ResourceUtils from "lib/ResourceUtils";
import { useSnackbar } from 'notistack';
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import React, { useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useTranslation } from 'react-i18next';
import { Location, useLocation, useNavigate } from "react-router";
import { RootPaths } from "routes/RootRouter";
import { AuthResult, AuthService } from "services/authService";
import { SessionService } from "services/sessionService";
import { AccountAgreement } from "./AccountAgreement";
import { AccountExpired } from "./AccountExpired";
import { AccountLockout } from "./AccountLockout";
import { AccountNotConfirmed } from "./AccountNotConfirmed";
import { AutoRegisterDialog } from "./AutoRegisterDialog";
import { ForgotPassDialog } from "./ForgotPassDialog";

////////////////////////////////////////////////////////////////////////////////

const RenderTopMessage: React.FC<{ firstLoad: boolean; }> = ({ firstLoad }) => {
  const location = useLocation();
  const search = location.search;
  const params = new URLSearchParams(search);

  const { t } = useTranslation();

  if (firstLoad) {

    if (params.get('confirmed')) {
      return <MprcMessageBlock center color="success">{t("Auth.Login.ConfirmedMessage")}</MprcMessageBlock>;
    }

    if (params.get('expired')) {
      return (
        <MprcMessageBlock center color="warning">
          <b>{t("Auth.Login.ExpiredMessage")}</b>
          <p>{t("Auth.Login.NewLoginRequiredMessage")}</p>
        </MprcMessageBlock>
      );
    }

    if (params.get('agree')) {
      return (
        <MprcMessageBlock center color="warning">
          <b>{t("Auth.Login.NotAgreementMessage")}</b>
        </MprcMessageBlock>
      );
    }

    if (location.state && (location.state as LocationState)?.reason != null) {
      return <MprcMessageBlock center color="info"><strong>{(location.state as LocationState)?.reason}</strong></MprcMessageBlock>;
    }

    if (location.state && (location.state as LocationState)?.signedOut === true) {
      return <MprcMessageBlock center color="info"><strong>{t("Auth.Login.LogoutMessage")}</strong></MprcMessageBlock>;
    }


    return null;
  }
};

////////////////////////////////////////////////////////////////////////////////
export interface LocationState {
  from: Location;
  signedOut: boolean;
  reason?: string;
}

export const LoginPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const session = useSession();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const api = useCoreApi();

  var ls = location.state as LocationState;
  const returnUrl = ls && ls.from ? ls.from.pathname + ls.from.search : RootPaths.Home;

  const [dialog, setDialog] = useState<any>(null);
  const [initializing, setInitializing] = useState<boolean>(true);
  const [firstSubmit, setFirstSubmit] = useState<boolean>(true);
  const [submitingPass, setSubmitingPass] = useState<boolean>(false);
  const [submitingSaml, setSubmitingSaml] = useState<boolean>(false);
  const [keepConnected, setKeepConnected] = useState<boolean>(true);
  const [authValue, setAuthValue] = useState<string | undefined>(undefined);
  const [password, setPassword] = useState<string | undefined>(undefined);
  const [showPass, setShowPass] = useState<boolean>(false);

  const [authResult, setAuthResult] = useState<AccountLoginCheckResult | null>(null);
  const [authResultMessage, setAuthResultMessage] = useState<string | null>(null);
  const [authStamp, setAuthStamp] = useState<string | null>(null);

  const acceptSaml = session.config?.samlOptions != null;
  const acceptPass = session.config?.authOptions != null;
  const mobileInfo = session.config?.mobileInfo;

  const authInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const lastSamlResponse = useRef<string | undefined>(undefined);
  const lastSubmitType = useRef<AccountAccessType | undefined>(undefined);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  ////////////////////////////////////////////////////////////////////////////

  ///
  const openSamlDialog = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    AuthService.openSamlDialog(session.config!.samlOptions!.samlLoginUrl!);
  };

  ///
  const handleSamlReceived = (event: any) => {
    event.preventDefault();
    handleSamlSubmit(event.detail);
  };

  ////////////////////////////////////////////////////////////////////////////

  ///
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.keyCode == 13) {
      event.preventDefault();
      handlePassSubmit();
    }
  };

  ///
  const loginComplete = () => {
    // Redireciona para a Home ou para o local que solicitou autenticação
    navigate(returnUrl, { replace: true });
  };

  ///
  const handleAgreementComplete = (accepted?: boolean) => {
    handleReset();
    if (accepted != undefined) {
      if (accepted) {
        if (lastSubmitType.current == AccountAccessType.Standard)
          handlePassSubmit();
        else if (lastSubmitType.current == AccountAccessType.Saml)
          handleSamlSubmit(lastSamlResponse.current!);
      }
      else
        session.clearSession();
    }
  };

  ///
  const handleReset = () => {
    setAuthResult(null);
    setAuthResultMessage(null);
    setAuthStamp(null);
  };

  ///
  const resetState = () => {
    setAuthResult(null);
    setAuthStamp(null);
    setAuthResultMessage(null);
    setFirstSubmit(false);
    setSubmitingPass(false);
    setSubmitingSaml(false);
    lastSamlResponse.current = undefined;
    lastSubmitType.current = undefined;
  };

  ///
  const clearValue = (value: string) => {
    return value.replace(/[\r\n]/g, '').trim();
  };

  ///
  const handleSubmit = async (
    p: {
      isValid: () => Boolean;
      signInCallback: () => Promise<AuthResult>;
    }) => {

    const { isValid, signInCallback } = p;

    if (!isValid())
      return;

    try {
      var recaptchaResult = 0;

      if (session.config.authOptions?.captchaSitekey != null) {

        if (recaptchaRef.current == null) throw t("Auth.Login.RecaptchaMissing");

        const token = await recaptchaRef.current.executeAsync();
        if (token == null) throw t("Auth.Login.RecaptchaTokenFailed");

        recaptchaResult = await (await api.v1.captcha.validateRecaptcha(token)).data;
      }

      if (recaptchaResult == 0) {
        const result = await signInCallback();
        if (result.loginStatus == AccountLoginCheckResult.Valid) {
          session.createSession(result.jwt!);
          loginComplete();
        }
        else if (result.loginStatus != undefined) {
          setAuthResult(result.loginStatus);
          setAuthStamp(result.continueAuthStamp);

          if (result.loginStatus == AccountLoginCheckResult.AccessTimeRestriction) {
            try {
              var res = JSON.parse(result.errorMessage!) as ResourceModel[];
              setAuthResultMessage(ResourceUtils.findName(res, session.uiCulture, session.config.defaultLocale) ?? t("Auth.Login.AccessTimeRestrictionMessage"));
            }
            catch {
              setAuthResultMessage(result.errorMessage ?? t("Auth.Login.AccessTimeRestrictionMessage"));
            }
          }
          else setAuthResultMessage(result.errorMessage);

          setFirstSubmit(false);
          setSubmitingPass(false);
          setSubmitingSaml(false);
          setTimeout(() => authInputRef?.current?.focus(), 0);
        }
        else {
          enqueueSnackbar(result.errorMessage);
          resetState();
          setTimeout(() => authInputRef?.current?.focus(), 0);
        }
      }
      else throw t("Auth.Login.Recaptcha" + recaptchaResult);
    }
    catch (e: any) {
      enqueueSnackbar(e.message, { variant: "error", preventDuplicate: true });
      resetState();
      setTimeout(() => authInputRef?.current?.focus(), 0);
    }
  };

  ///
  const handlePastePass = (event: ClipboardEvent) => {
    event.preventDefault();
    const paste = event.clipboardData?.getData("text/plain");
    if (paste) {
      passwordInputRef.current!.value = clearValue(paste);
      setPassword(paste);
    }
  };

  ///
  const handlePasteAuth = (event: ClipboardEvent) => {
    event.preventDefault();
    const paste = event.clipboardData?.getData("text/plain");
    if (paste) {
      authInputRef.current!.value = clearValue(paste);
      setAuthValue(paste);
    }
  };

  ///
  const handlePassSubmit = async () => {
    lastSubmitType.current = AccountAccessType.Standard;

    await handleSubmit(
      {
        isValid: () => authValue?.length != 0 && password?.length != 0,
        signInCallback: () => {
          setSubmitingPass(true);
          setSubmitingSaml(false);
          return AuthService.passwordSignIn({ authValue: authValue!, password: password! });
        }
      }
    );
  };

  ///
  const handleSamlSubmit = async (samlResponse: string) => {
    lastSubmitType.current = AccountAccessType.Saml;
    lastSamlResponse.current = samlResponse;

    await handleSubmit(
      {
        isValid: () => samlResponse != null && samlResponse.length > 0,
        signInCallback: () => {
          setSubmitingPass(false);
          setSubmitingSaml(true);
          return AuthService.samlSignIn(samlResponse);
        }
      }
    );
  };

  ////////////////////////////////////////////////////////////////////////////

  ///
  const loginForm = () => {
    return (
      <MprcCard sx={{ maxWidth: 450 }} >
        <form autoComplete="on" spellCheck={false}>

          <CardContent>

            {
              authResultMessage &&
              <Alert severity="error" variant="filled" >
                {authResultMessage}
              </Alert>
            }

            {
              acceptPass &&
              <>
                <TextField
                  id="performaId"
                  label={session.config.authOptions?.identifierInstructions ?? t("Auth.Login.IdentifierLabel")}
                  autoComplete="performaId"
                  margin="normal"
                  fullWidth={true}
                  error={authValue != undefined && authValue.length == 0}
                  disabled={submitingPass || submitingSaml}
                  value={authValue || ""}
                  inputRef={authInputRef}
                  slotProps={{
                    input: {
                      inputProps: { maxLength: 255 },
                      onKeyDown: (e) => handleKeyDown(e)
                    },
                    inputLabel: {
                      shrink: true
                    }
                  }}
                  onChange={
                    (e) => {
                      setAuthValue(e.target.value);
                    }
                  }
                />

                <TextField
                  id="pass"
                  name="password"
                  label={t("Auth.Login.PasswordLabel")}
                  type={showPass ? undefined : "password"}
                  autoComplete="performaPass"
                  InputLabelProps={{ shrink: true }}
                  error={password != null && password.length == 0}
                  margin="normal"
                  fullWidth={true}
                  disabled={submitingPass || submitingSaml}
                  value={password || ""}
                  inputRef={passwordInputRef}
                  slotProps={{
                    input: {
                      inputProps: {
                        maxLength: 255,
                        onKeyDown: (e) => handleKeyDown(e)
                      },
                      endAdornment: <FontAwesomeIcon
                        className={AppGlobalClassKeys.clickable}
                        icon={showPass ? faEye : faEyeSlash}
                        fixedWidth
                        onClick={() => setShowPass(!showPass)}
                      />
                    }
                  }}
                  onChange={(e) => setPassword(e.target.value)}
                />

                <Grid container justifyContent="space-between">
                  <Grid>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={keepConnected}
                          onChange={
                            () => {
                              window.keepConnected = !keepConnected;
                              setKeepConnected(!keepConnected);
                            }
                          }
                          value=""
                        />
                      }
                      label={t("Auth.Login.KeepConnectedLabel")}
                      title={t("Auth.Login.KeepConnectedTitle")}
                    />
                  </Grid>
                  <Grid>
                    <Button
                      type="button"
                      color="primary"
                      variant="contained"
                      disabled={submitingPass || submitingSaml || (authValue ?? "").length == 0 || (password ?? "").length == 0}
                      onClick={
                        (e: React.MouseEvent<HTMLButtonElement>) => {
                          e.preventDefault();
                          handlePassSubmit();
                        }
                      }
                    >
                      <span>{t("Auth.Login.SubmitText")}</span>
                      {submitingPass && <MprcSpinnerIndicator />}
                    </Button>

                  </Grid>
                </Grid>
              </>
            }

            {
              acceptPass && acceptSaml &&
              <Grid container justifyContent="center" alignItems="center" sx={{ mt: 3 }}>
                <Grid>
                  <MprcLine width={80} />
                  <MprcTextMuted>&nbsp;OU&nbsp;</MprcTextMuted>
                  <MprcLine width={80} />
                </Grid>
              </Grid>
            }

            {
              acceptSaml &&
              <Grid container direction="column" justifyContent="space-between" alignItems="center">
                {
                  <Grid size="grow" sx={{ mt: 3 }}>
                    <Button
                      color="success"
                      variant="contained"
                      size="large"
                      disabled={submitingPass || submitingSaml}
                      onClick={openSamlDialog}
                    >
                      <span>{t("Auth.Login.EnterWithSml")}</span>
                      {submitingSaml && <MprcSpinnerIndicator />}
                    </Button>
                  </Grid>
                }
              </Grid>
            }

            {
              (acceptPass && (session.config?.authOptions?.enablePasswordRecovery === true || session.config?.authOptions?.enableAutoRegister === true)) &&
              <Grid container justifyContent="space-evenly" sx={{ mt: 3 }}>
                <Grid>
                  <Container>
                    {
                      session.config.authOptions.enablePasswordRecovery &&
                      <MprcLink
                        onClick={
                          () => setDialog(
                            <ForgotPassDialog
                              onClose={() => setDialog(null)}
                            />
                          )
                        }
                      >
                        {t("Auth.Login.ForgotPasswordText")}
                      </MprcLink>
                    }
                    {
                      session.config.authOptions.enablePasswordRecovery &&
                      session.config?.authOptions.enableAutoRegister === true &&
                      <span>&nbsp;&nbsp;|&nbsp;&nbsp;</span>
                    }
                    {
                      session.config?.authOptions.enableAutoRegister === true &&
                      <MprcLink
                        onClick={
                          () => setDialog(
                            <AutoRegisterDialog
                              onClose={() => setDialog(null)}
                            />
                          )
                        }
                      >
                        {t("Auth.Login.CreateAccountText")}
                      </MprcLink>
                    }
                  </Container>
                </Grid>
              </Grid>
            }

          </CardContent>

          <MprcCardActions>
          </MprcCardActions>
        </form>
      </MprcCard>
    );
  };

  ///
  const buildMainForm = () => {
    return (
      <>
      {!!mobileInfo?.android?.androidPackageName && !!mobileInfo?.ios?.iosAppStoreUrl && (
        <div
          style={
            {
              position: "absolute",
              bottom: 0,
              right: 0,
            }
          }>
          <Stack >
            <a href={mobileInfo.ios.iosAppStoreUrl} target="_blank" rel="noreferrer"><img src="/img/app/App-Store-Apple.png" /></a>
            <a href={`${APP_STORE_GOOGLE_LINK}${mobileInfo.android.androidPackageName}`} target="_blank" rel="noreferrer"><img src="/img/app/App-Store-GooglePlay.png" /></a>
          </Stack>
        </div>
        )}

        {
          dialog ||
          (
            <Grid container direction="column" justifyContent="center" alignItems="center" spacing={2} >
              <Grid size="grow">
                <img
                  src={SessionService.current.appImages.highlightImageUrl}
                  width="360"
                />
              </Grid>
              <Grid size="grow">
                <RenderTopMessage firstLoad={firstSubmit} />
                {
                  authResult != null && authResult == AccountLoginCheckResult.Lockedout
                    ? <AccountLockout onClose={handleReset} />
                    : authResult != null && authResult == AccountLoginCheckResult.AccountExpired
                      ? <AccountExpired onClose={handleReset} />
                      : authResult != null && authResult == AccountLoginCheckResult.NotConfirmed
                        ? <AccountNotConfirmed authValue={authValue!} onClose={handleReset} />
                        : authResult != null && authStamp != null && (authResult == AccountLoginCheckResult.NotAgreement || authResult == AccountLoginCheckResult.AgreementUpdated)
                          ? <AccountAgreement securityStamp={authStamp} reason={authResult == AccountLoginCheckResult.NotAgreement ? "NotAssigned" : "Update"} onComplete={handleAgreementComplete} />
                          : loginForm()
                }
              </Grid>
            </Grid>
          )
        }

        {
          session.config.authOptions?.captchaSitekey &&
          <ReCAPTCHA
            id="captchaID"
            ref={recaptchaRef}
            size="invisible"
            sitekey={session.config.authOptions?.captchaSitekey}
            badge="bottomleft"
            onExpired={() => recaptchaRef.current?.reset()}
            onErrored={() => alert("Recaptcha error")}
          />
        }
      </>
    );
  };

  ////////////////////////////////////////////////////////////////////////////

  useEffect(() => {
    if (initializing) {
      if (acceptSaml) {
        document.addEventListener('saml', handleSamlReceived, false);
      }

      setTimeout(() => {
        authInputRef?.current?.focus();
      }, 0);

      setInitializing(false);
    }

    return () => {
      if (acceptSaml) {
        document.removeEventListener('saml', handleSamlReceived, false);
      }
    };
  }, []);

  /// Listener para PASTE na Senha
  useEffect(() => {
    if (passwordInputRef.current) passwordInputRef.current.addEventListener("paste", handlePastePass);

    return () => {
      if (passwordInputRef.current) passwordInputRef.current.removeEventListener("paste", handlePastePass);
    };
  }, [passwordInputRef.current]);


  /// Listener para PASTE no ID
  useEffect(() => {
    if (authInputRef.current) authInputRef.current.addEventListener("paste", handlePasteAuth);

    return () => {
      if (authInputRef.current) authInputRef.current.removeEventListener("paste", handlePasteAuth);
    };
  }, [passwordInputRef.current]);

  ////////////////////////////////////////////////////////////////////////////

  return initializing
    ? <MprcRequestIndicator type="waiting" />
    : buildMainForm();
};
